/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Dashboard Service - Super Admin Dashboard API
 * ============================================================================
 *
 * Service layer for Super Admin dashboard API calls.
 *
 * NOTE: Notification-related functionality (attention items, activity items)
 * has been moved to the extensible notification system.
 * See: src/services/notification.service.ts
 * See: src/hooks/api/useNotifications.ts
 *
 * This service now focuses on dashboard statistics only.
 *
 * ============================================================================
 */

import { get } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import {
  PlatformStats,
  ActivityItem,
  AttentionItem,
  DashboardSummary,
} from '@/types/superadmin.types';
import { withdrawalsService } from './withdrawals.service';

const BASE_URL = '/admin/dashboard';

/**
 * Backend stats structure (may differ from frontend PlatformStats)
 */
interface BackendStats {
  totalOrganizations: number;
  activeOrganizations: number;
  trialOrganizations: number;
  suspendedOrganizations: number;
  pendingApproval: number;
  totalUsers: number;
  activeUsers?: number;
  activeSessions?: number;
  monthlyRecurringRevenue?: number | string;
  monthlyRevenue?: number | string;
  activeMrr?: number | string;      // MRR from ACTIVE subscriptions
  trialMrr?: number | string;       // MRR from TRIAL subscriptions
  subscriptionCounts?: Record<string, number>;
  subscriptionRevenue?: Record<string, number | string>;
  orgsByTier?: Record<string, number>;
  totalPartners?: number;
  activePartners?: number;
}

/**
 * Transform backend stats to frontend PlatformStats format
 */
function transformStats(backend: BackendStats): PlatformStats {
  // Default tiers (using PROFESSIONAL as the mid-tier)
  const defaultTiers = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

  // Transform subscriptionRevenue values from string/number to number
  const subscriptionRevenue: Record<string, number> = {};
  if (backend.subscriptionRevenue) {
    for (const [tier, value] of Object.entries(backend.subscriptionRevenue)) {
      subscriptionRevenue[tier] = typeof value === 'string' ? parseFloat(value) : (value || 0);
    }
  }
  // Ensure all tiers have values
  for (const tier of defaultTiers) {
    if (subscriptionRevenue[tier] === undefined) {
      subscriptionRevenue[tier] = 0;
    }
  }

  // Use subscriptionCounts or orgsByTier
  const subscriptionCounts: Record<string, number> = {
    ...(backend.subscriptionCounts || backend.orgsByTier || {})
  };
  // Ensure all tiers have values
  for (const tier of defaultTiers) {
    if (subscriptionCounts[tier] === undefined) {
      subscriptionCounts[tier] = 0;
    }
  }

  // Get MRR value
  const mrr = backend.monthlyRecurringRevenue || backend.monthlyRevenue || 0;
  const mrrValue = typeof mrr === 'string' ? parseFloat(mrr) : mrr;

  // Get activeMrr and trialMrr values
  const activeMrrRaw = backend.activeMrr || 0;
  const trialMrrRaw = backend.trialMrr || 0;
  const activeMrrValue = typeof activeMrrRaw === 'string' ? parseFloat(activeMrrRaw) : activeMrrRaw;
  const trialMrrValue = typeof trialMrrRaw === 'string' ? parseFloat(trialMrrRaw) : trialMrrRaw;

  return {
    totalOrganizations: backend.totalOrganizations,
    activeOrganizations: backend.activeOrganizations,
    trialOrganizations: backend.trialOrganizations,
    suspendedOrganizations: backend.suspendedOrganizations,
    pendingApproval: backend.pendingApproval || 0,
    totalUsers: backend.totalUsers,
    totalPartners: backend.totalPartners,
    activePartners: backend.activePartners,
    monthlyRecurringRevenue: mrrValue,
    activeMrr: activeMrrValue,
    trialMrr: trialMrrValue,
    subscriptionCounts,
    subscriptionRevenue,
  };
}

/**
 * Build attention items from stats data.
 *
 * @deprecated This function is kept for backward compatibility.
 * Use the notification system instead: useNotifications() hook
 * which fetches from GET /api/v1/notifications
 *
 * Note: title and description use translation keys that must be resolved by the component
 */
function buildAttentionItems(
  stats: PlatformStats,
  withdrawalStats?: { pendingCount: number; pendingAmount: number }
): AttentionItem[] {
  const items: AttentionItem[] = [];

  // Pending registrations/approvals
  if (stats.pendingApproval > 0) {
    items.push({
      type: 'PENDING_REGISTRATION',
      title: 'pendingRegistration',
      description: 'pendingRegistration',
      count: stats.pendingApproval,
      urgency: stats.pendingApproval >= 5 ? 'HIGH' : stats.pendingApproval >= 2 ? 'MEDIUM' : 'LOW',
    });
  }

  // Suspended organizations (failed payments)
  if (stats.suspendedOrganizations > 0) {
    items.push({
      type: 'FAILED_PAYMENT',
      title: 'suspendedOrg',
      description: 'suspendedOrg',
      count: stats.suspendedOrganizations,
      urgency: 'HIGH',
    });
  }

  // Pending withdrawals
  if (withdrawalStats && withdrawalStats.pendingCount > 0) {
    items.push({
      type: 'PENDING_WITHDRAWAL',
      title: 'pendingWithdrawal',
      description: 'pendingWithdrawal',
      count: withdrawalStats.pendingCount,
      urgency: withdrawalStats.pendingCount >= 5 ? 'HIGH' : withdrawalStats.pendingCount >= 2 ? 'MEDIUM' : 'LOW',
      meta: { amount: withdrawalStats.pendingAmount },
    });
  }

  // Expiring trials
  if (stats.trialOrganizations > 0) {
    items.push({
      type: 'EXPIRING_TRIAL',
      title: 'expiringTrial',
      description: 'expiringTrial',
      count: stats.trialOrganizations,
      urgency: 'LOW',
    });
  }

  return items;
}

/**
 * Dashboard service for Super Admin
 */
export const dashboardService = {
  /**
   * Get complete dashboard summary by aggregating multiple endpoints.
   *
   * @deprecated For notifications, use the new notification system:
   * - useNotifications() hook for the bell icon
   * - GET /api/v1/notifications endpoint
   *
   * This method is kept for the dashboard page which still needs stats.
   */
  async getDashboardSummary(): Promise<DashboardSummary> {
    // Fetch stats, activity, and withdrawal stats in parallel
    const [stats, activity, withdrawalStats] = await Promise.all([
      this.getStats(),
      this.getRecentActivity(10),
      withdrawalsService.getWithdrawalStats().catch(() => null), // Graceful fallback
    ]);

    // Build attention items from real data
    const attentionItems = buildAttentionItems(
      stats,
      withdrawalStats || undefined
    );

    return {
      stats,
      recentActivity: activity,
      attentionItems,
    };
  },

  /**
   * Get platform statistics
   */
  async getStats(): Promise<PlatformStats> {
    const response = await get<ApiResponse<BackendStats>>(`${BASE_URL}/stats`);
    return transformStats(response.data);
  },

  /**
   * Get recent activity feed
   */
  async getRecentActivity(limit: number = 10): Promise<ActivityItem[]> {
    const response = await get<ApiResponse<ActivityItem[]>>(`${BASE_URL}/activity`, { limit });
    return response.data;
  },

  /**
   * Get organizations grouped by subscription tier
   */
  async getOrganizationsByTier(): Promise<Record<string, number>> {
    const response = await get<ApiResponse<Record<string, number>>>(`${BASE_URL}/organizations-by-tier`);
    return response.data;
  },

  /**
   * Get security metrics
   */
  async getSecurityMetrics(): Promise<Record<string, unknown>> {
    const response = await get<ApiResponse<Record<string, unknown>>>(`${BASE_URL}/security`);
    return response.data;
  },
};

export default dashboardService;
